import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, CircleNotch, Warning } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [clubName, setClubName] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          setClubName(res.data.metadata?.club_name || "");
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 8) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <a href="/" className="font-heading font-black text-xl uppercase tracking-tight text-[#0A0A0A] inline-block mb-10">
          targetfunder<span className="text-[#BBB]">.com</span>
        </a>

        {status === "checking" && (
          <div data-testid="payment-checking">
            <CircleNotch size={40} className="animate-spin text-[#0A0A0A] mx-auto mb-5" />
            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-[#0A0A0A] mb-2">
              Confirming Payment
            </h1>
            <p className="font-body text-sm text-[#666]">Please wait while we verify your payment...</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle size={48} weight="duotone" className="text-green-600 mx-auto mb-5" />
            <h1 className="font-heading font-bold text-3xl uppercase tracking-tight text-[#0A0A0A] mb-3">
              Payment Confirmed
            </h1>
            {clubName && (
              <p className="font-body text-lg text-[#0A0A0A] font-semibold mb-2">{clubName}</p>
            )}
            <p className="font-body text-base text-[#666] leading-relaxed mb-6">
              Thank you! We've received your details and payment. We'll start building your branded fundraiser right away and have it live within 24 hours.
            </p>
            <p className="font-body text-sm text-[#999]">
              We'll email you the link as soon as it's ready.
            </p>
          </div>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <div data-testid="payment-error">
            <Warning size={48} weight="duotone" className="text-orange-500 mx-auto mb-5" />
            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-[#0A0A0A] mb-3">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="font-body text-base text-[#666] leading-relaxed mb-6">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We couldn't confirm your payment. If you were charged, don't worry — email us and we'll sort it out."}
            </p>
            <a
              href="mailto:enquiries@targetfunder.com"
              className="font-body text-sm text-[#0A0A0A] underline"
            >
              enquiries@targetfunder.com
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
